import React, { useState } from "react";
import styled from "styled-components";
import { IconButton, useToaster } from "rsuite";
import EditIcon from "@rsuite/icons/Edit";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "../../../../../ui/Modal/Modal";
import Button from "../../../../../ui/Button/Button";
import Input from "../../../../../ui/Input/Input";
import { colors } from "../../../../../ui/theme/colors";

export const EditSite = ({ site, update, setUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const initFormSite = {
    id: site.id,
    name: site.name,
    address: site.street_address,
    city: site.city,
    state: site.state,
    zip: site.zip,
  };
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormValue(initFormSite); // Reset form when closing
  };
  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState(initFormSite);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (name, value) => {
    setFormValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/update/site/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: site.customer_id,
          site: formValue,
        }),
      });

      setUpdate(!update);
      toaster.push(successMessage, "topCenter");
      handleClose();
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  const footerActions = (
    <FooterButtons>
      <Button variant="subtle" onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onSubmit} loading={loading}>
        Update Site
      </Button>
    </FooterButtons>
  );

  return (
    <>
      <StyledIconButton icon={<EditIcon />} onClick={handleOpen} />
      <Modal
        size="sm"
        open={open}
        onClose={handleClose}
        title={`Edit Site: ${site?.name}`}
        showFooter={true}
        footerActions={footerActions}
        loading={loading}
      >
        <FormContainer>
          <FormRow>
            <Input
              label="Site ID"
              value={formValue.id}
              disabled
              helpText="Site ID cannot be changed"
            />
          </FormRow>

          <FormRow>
            <Input
              label="Site Name"
              value={formValue.name}
              onChange={(value) => handleInputChange("name", value)}
              placeholder="Enter site name"
            />
          </FormRow>

          <FormRow>
            <Input
              label="Street Address"
              value={formValue.address}
              onChange={(value) => handleInputChange("address", value)}
              placeholder="Enter street address"
            />
          </FormRow>

          <FormRowGroup>
            <FormColumn>
              <Input
                label="City"
                value={formValue.city}
                onChange={(value) => handleInputChange("city", value)}
                placeholder="Enter city"
              />
            </FormColumn>

            <FormColumn>
              <Input
                label="State"
                value={formValue.state}
                onChange={(value) => handleInputChange("state", value)}
                placeholder="Enter state"
              />
            </FormColumn>
          </FormRowGroup>

          <FormRow>
            <Input
              label="ZIP Code"
              value={formValue.zip}
              onChange={(value) => handleInputChange("zip", value)}
              placeholder="Enter ZIP code"
            />
          </FormRow>
        </FormContainer>
      </Modal>
    </>
  );
};

const StyledIconButton = styled(IconButton)`
  .rs-btn {
    border-radius: 50%;
    padding: 8px;
    min-width: 36px;
    height: 36px;
    border: 2px solid ${colors.primary.light};
    background: rgba(44, 90, 160, 0.1);
    color: ${colors.primary.main};
    transition: all 0.3s ease;

    &:hover {
      background: ${colors.primary.main};
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(44, 90, 160, 0.3);
    }

    .rs-icon {
      font-size: 16px;
    }
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 0;
`;

const FormRow = styled.div`
  width: 100%;
`;

const FormRowGroup = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const FormColumn = styled.div`
  flex: 1;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
`;

const successMessage = {
  type: "success",
  header: "Success",
  description: "Site updated successfully!",
};

const errorMessage = {
  type: "error",
  header: "Error",
  description: "Something went wrong. Please try again.",
};
